import React from 'react';
import { Field, ErrorMessage, useField } from 'formik';
import { getBallastTypeError } from '../../../../services/BallastCbrDatabase';
import { FieldConfig } from '../../../../services/ValidationService';
import { useDynamicSelectOptions } from '../../../hooks/useDynamicSelectOptions';
import { renderErrorMessage as defaultRenderErrorMessage } from './utils';

interface DynamicSelectFieldProps {
    nestedFieldName: string;
    fieldConfig: FieldConfig;
    isMethodSelect: boolean;
    isDisabled: boolean;
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue: (field: string, value: unknown) => void, fieldName: string) => void;
    setFieldValue: (field: string, value: unknown) => void;
    fieldName: string;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    t: (key: string, options?: Record<string, unknown>) => string;
    renderErrorMessage?: (msg: string) => React.ReactElement;
}

const DynamicSelectField: React.FC<DynamicSelectFieldProps> = ({
    nestedFieldName,
    fieldConfig,
    isDisabled,
    handleInputChange,
    setFieldValue,
    fieldName,
    t,
    renderErrorMessage = defaultRenderErrorMessage,
}) => {
    // Use hook for dynamic options
    const [field] = useField<unknown>(nestedFieldName);
    const ballastError = fieldConfig.name === 'ballastType' ? getBallastTypeError(field.value) : undefined;
    const invalidValue = typeof field.value === 'string' ? field.value : '';
    const errorId = `${nestedFieldName}-error`;
    const { options, loading } = useDynamicSelectOptions(fieldConfig.optionsSource, fieldConfig.options);

    return (
        <div className="field-container" data-testid={`field-${nestedFieldName}`}>
            <Field
                as="select"
                id={nestedFieldName}
                name={nestedFieldName}
                aria-invalid={ballastError ? true : undefined}
                aria-describedby={ballastError ? errorId : undefined}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    // Always explicitly call setFieldValue to ensure nested field names work correctly
                    // Formik's default behavior may not properly handle nested names like "drainage.baseDrainageType"
                    setFieldValue(nestedFieldName, e.target.value);
                    // Call custom handler if provided - it contains cascade logic for materials, geogrids, traffic, etc.
                    if (handleInputChange) {
                        handleInputChange(e, setFieldValue, fieldName);
                    }
                }}
                disabled={isDisabled || loading}>
                {ballastError && (
                    <option value={invalidValue} disabled>
                        {invalidValue.trim() ? invalidValue : t('select_ballast_type')}
                    </option>
                )}
                {loading && <option value="">Loading...</option>}
                {!loading && options.length === 0 && <option value="">No options available</option>}
                {!loading && options.length > 0 && !fieldConfig.required && <option value="">{t('none_selected', {}) || 'None'}</option>}
                {!loading &&
                    options.map(option => (
                        <option key={option.value} value={option.value}>
                            {t(option.label)}
                        </option>
                    ))}
            </Field>
            {ballastError ? (
                <div id={errorId} role="alert">
                    {ballastError}
                </div>
            ) : (
                <ErrorMessage name={nestedFieldName} render={renderErrorMessage} />
            )}
        </div>
    );
};

export default DynamicSelectField;
